import React, { useState } from "react";
import { useDispatch } from "react-redux";
import debounce from "lodash.debounce";

import { SkedIcon } from "ui";
import { Link } from "ui/Link";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { hideModal } from "actions/modal";
import { editPostUrl, reloadPosts } from "actions/postsView";
import { isUrl } from "utils/strings";
import { Post as PostType } from "features/post/types";

import "./SkedUrlModal.css";

interface SkedUrlModalProps {
  post: PostType;
  isSkedLinkActive: boolean;
}

const SkedUrlModal = ({
  post,
  isSkedLinkActive,
}: SkedUrlModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const [urlValue, setUrlValue] = useState(post.redirectUrl || "");
  const [isValidUrl, setIsValidUrl] = useState(() => isUrl(urlValue));

  const handleChangeDebounced = debounce((url) => {
    const isUrlValid = isUrl(url);
    setIsValidUrl(isUrlValid);
  }, 1000);

  const handleSwitchClick = () => {
    dispatch(editPostUrl({ ...post, redirectUrl: urlValue }));
    dispatch(hideModal());
    dispatch(reloadPosts());
  };

  const handleClose = () => {
    dispatch(hideModal());
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value?.trim();
    setUrlValue(url);
    handleChangeDebounced(url);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        contentWrapperMode="custom"
        showCloseButton={false}
        className="w-[560px] max-w-[calc(100vw-2rem)] rounded-[8px] border border-neutral-100 bg-white p-6 shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.1),0px_2px_4px_-2px_rgba(16,24,40,0.06)]"
      >
        <div className="flex flex-col gap-6">
          <DialogHeader className="gap-2 p-0 text-left">
            <DialogTitle className="sked-url-modal__title m-0 text-2xl font-bold leading-8 text-neutral-800">
              {post.redirectUrl ? "Update URL" : "Add a URL"} to this post in
              SkedLink
            </DialogTitle>
            <DialogDescription className="m-0 font-inter text-base font-normal leading-6 text-neutral-800">
              {`Outdated product or website link in your SkedLink${
                isSkedLinkActive ? "" : " (link in bio tool)"
              }? Update the URL that this post directs to.`}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 font-inter text-neutral-800">
            {!isSkedLinkActive && (
              <div className="flex gap-2 rounded-[8px] bg-blue-50 p-3">
                <SkedIcon
                  icon="info"
                  size="18"
                  className="shrink-0 text-blue-500"
                />
                <p className="m-0 text-sm leading-5 text-blue-600">
                  This feature is for SkedLink - Sked's link in bio tool. It
                  appears you haven't set it up yet (or maybe you're using a
                  link shortener). You'll still be able to save a URL, it just
                  won't be active until your SkedLink is active.{" "}
                  <Link
                    href="https://help.skedsocial.com/en/articles/3811771-getting-started-with-sked-link"
                    target="_blank"
                    underline="always"
                    className="text-blue-600 underline hover:text-blue-700"
                  >
                    Read how to set up SkedLink here
                  </Link>
                  .
                </p>
              </div>
            )}

            <div className="flex flex-col gap-1">
              <label
                htmlFor="sked-link-url"
                className="font-inter text-sm font-medium leading-5 text-neutral-800"
              >
                {post.redirectUrl ? "Update URL" : "Add a URL"} here (website or
                product URL for this post{" "}
                <Link
                  href="https://help.skedsocial.com/en/articles/4129487-how-links-work-in-sked"
                  target="_blank"
                  className="text-blue-500 underline hover:text-blue-600"
                >
                  read about how links work
                </Link>
                ):
              </label>

              <Input
                id="sked-link-url"
                onChange={handleTextInputChange}
                placeholder="URL for this post (include http:// or https://)"
                value={urlValue}
                className="h-10 rounded-[8px] border-neutral-100 px-[14px] py-[10px] font-inter text-sm font-normal leading-5 text-neutral-800 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] placeholder:text-neutral-500"
              />
            </div>
          </div>

          <DialogFooter className="flex-row justify-end gap-2 p-0 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="black-secondary"
              size="sm"
              onClick={handleClose}
              className="rounded-[8px] border-neutral-100 px-3 py-1.5 text-neutral-800 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="blue-primary"
              size="sm"
              onClick={handleSwitchClick}
              disabled={!isValidUrl}
              className={`rounded-[8px] px-3 py-1.5 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] ${
                isValidUrl ? "" : "opacity-40"
              }`}
            >
              Save
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SkedUrlModal;
